import { useId, useState } from "react";
import { motion, type Transition } from "motion/react";
import { cn } from "../cn";
import type { LogoColor, LogoVariant } from "./one-nexty";

export interface NextyBrandLogoProps extends React.SVGProps<SVGSVGElement> {
  variant?: LogoVariant | "wordmark";
  color?: LogoColor;
  size?: number;
  title?: string;
  className?: string;
}

const springTransition: Transition = {
  type: "spring",
  stiffness: 350,
  damping: 20,
};

/**
 * Emblème / Symbole Produit nexty (Glyphe "n" dynamique + Nœud d'impulsion)
 */
export function NextyBrandMark({
  color = "primary",
  size = 32,
  title = "nexty Brand Symbol",
  className,
  ...props
}: Omit<NextyBrandLogoProps, "variant">) {
  const rawGradientId = useId();
  const rawDotGlowId = useId();

  const gradientId = rawGradientId.replace(/:/g, "_");
  const dotGlowId = rawDotGlowId.replace(/:/g, "_");

  const isGradient = color === "gradient" || color === "primary";
  const isMono = color === "monochrome";
  const isWhite = color === "white";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 36 36"
      fill="none"
      width={size}
      height={size}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      className={cn("inline-block shrink-0 align-middle", className)}
      {...props}
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2563EB" />
          <stop offset="60%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>

        <radialGradient id={dotGlowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.85" />
          <stop offset="35%" stopColor="#3B82F6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Courbe fluide du 'n' technologique */}
      <path
        d="M9 28V14.5C9 10.91 11.91 8 15.5 8C19.09 8 22 10.91 22 14.5V28"
        stroke={isWhite ? "#FFFFFF" : isMono ? "currentColor" : `url(#${gradientId})`}
        strokeWidth="3.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Trait dynamique d'accélération (Futur / Prochaine étape) */}
      <path
        d="M22 19.5L27.5 14C28.88 12.62 31 13.6 31 15.55V28"
        stroke={isWhite ? "#FFFFFF" : isMono ? "currentColor" : `url(#${gradientId})`}
        strokeWidth="3.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Halo de diffusion bleu véritablement évanescent */}
      {isGradient && (
        <circle cx="31" cy="8.5" r="5.5" fill={`url(#${dotGlowId})`} />
      )}

      {/* Point bleu royal intense d'accentuation (Le point de "nexty.") */}
      <circle
        cx="31"
        cy="8.5"
        r="2.5"
        fill="#2563EB"
      />
    </svg>
  );
}

/**
 * Wordmark produit "nexty." seul
 */
export function NextyWordmark({
  color = "primary",
  size = 28,
  className,
}: {
  color?: LogoColor;
  size?: number;
  className?: string;
}) {
  const isWhite = color === "white";
  const isMono = color === "monochrome";

  const textColor = isWhite ? "text-white" : isMono ? "text-current" : "text-fg dark:text-white";

  return (
    <span
      className={cn(
        "font-extrabold tracking-tight font-sans inline-flex items-baseline select-none",
        textColor,
        className
      )}
      style={{ fontSize: `${size}px` }}
    >
      nexty
      <span className="text-blue-600 dark:text-blue-500" style={{ color: "#2563EB" }}>.</span>
    </span>
  );
}

/**
 * Logo produit complet nexty (Mark + Wordmark)
 */
export function NextyBrandLogo({
  variant = "full",
  color = "primary",
  size = 32,
  title = "nexty",
  className,
  ...props
}: NextyBrandLogoProps) {
  if (variant === "mark") {
    return <NextyBrandMark color={color} size={size} title={title} className={className} {...props} />;
  }

  if (variant === "wordmark") {
    return <NextyWordmark color={color} size={size} className={className} />;
  }

  const isStacked = variant === "stacked";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2.5 select-none",
        isStacked ? "flex-col text-center gap-1.5" : "flex-row",
        className
      )}
    >
      <NextyBrandMark color={color} size={size} title={title} {...props} />
      <NextyWordmark color={color} size={size * 0.85} />
    </div>
  );
}

/**
 * App Icon Squircle (Format d'application iOS / macOS / App Store)
 */
export interface NextyAppIconProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
  className?: string;
}

export function NextyAppIcon({ size = 64, className, ...props }: NextyAppIconProps) {
  return (
    <div
      className={cn(
        "relative inline-flex items-center justify-center overflow-hidden rounded-[22%] bg-gradient-to-br from-slate-900 via-slate-950 to-zinc-950 shadow-xl shadow-blue-950/20 border border-slate-800/80 transition-transform hover:scale-105",
        className
      )}
      style={{ width: `${size}px`, height: `${size}px` }}
      {...props}
    >
      {/* Reflet lumineux supérieur */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
      <NextyBrandMark size={Math.round(size * 0.58)} color="gradient" />
    </div>
  );
}

/**
 * Favicon optimisé pour les petits formats (16px, 32px, 48px)
 */
export function NextyFavicon({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center rounded-lg bg-slate-950 p-1 border border-blue-500/30 shadow-sm",
        className
      )}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <NextyBrandMark size={Math.round(size * 0.75)} color="gradient" />
    </div>
  );
}

/**
 * Logo produit nexty Animé (Hover interaction, Morphs et Pulse)
 */
export interface AnimatedNextyLogoProps extends NextyBrandLogoProps {
  autoAnimate?: boolean;
}

export function AnimatedNextyLogo({
  variant = "full",
  color = "primary",
  size = 36,
  autoAnimate = false,
  className,
  ...props
}: AnimatedNextyLogoProps) {
  const [isHovered, setIsHovered] = useState(false);
  const gradientId = useId();
  const isActive = autoAnimate || isHovered;

  const isWhite = color === "white";
  const isMono = color === "monochrome";

  const textColor = isWhite ? "text-white" : isMono ? "text-current" : "text-fg dark:text-white";

  const markComponent = (
    <motion.div
      className="relative inline-block cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.08, rotate: [0, -3, 3, 0] }}
      whileTap={{ scale: 0.94 }}
      transition={springTransition}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 36 36"
        fill="none"
        width={size}
        height={size}
        className="overflow-visible"
        {...props}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="50%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#4F46E5" />
          </linearGradient>
        </defs>

        {/* Halo lumineux d'accent */}
        <motion.circle
          cx="18"
          cy="18"
          r="14"
          fill={`url(#${gradientId})`}
          animate={{
            scale: isActive ? [1, 1.3, 1] : 1,
            opacity: isActive ? [0.15, 0.35, 0.15] : 0.08,
          }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "18px 18px" }}
        />

        {/* Tracé principal du "n" */}
        <motion.path
          d="M9 28V14.5C9 10.91 11.91 8 15.5 8C19.09 8 22 10.91 22 14.5V28"
          stroke={`url(#${gradientId})`}
          strokeWidth="3.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            pathLength: isActive ? 1 : 0.9,
          }}
          transition={{ duration: 0.4 }}
        />

        <motion.path
          d="M22 19.5L27.5 14C28.88 12.62 31 13.6 31 15.55V28"
          stroke={`url(#${gradientId})`}
          strokeWidth="3.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            pathLength: isActive ? 1 : 0.9,
          }}
          transition={{ duration: 0.4, delay: 0.1 }}
        />

        {/* Point bleu royal rebondissant */}
        <motion.circle
          cx="31"
          cy="8.5"
          r="2.75"
          fill="#2563EB"
          animate={{
            y: isActive ? [0, -3, 0] : 0,
            scale: isActive ? [1, 1.3, 1] : 1,
          }}
          transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "31px 8.5px" }}
        />
      </svg>
    </motion.div>
  );

  if (variant === "mark") {
    return markComponent;
  }

  const isStacked = variant === "stacked";

  return (
    <motion.div
      className={cn(
        "inline-flex items-center gap-2.5 cursor-pointer select-none",
        isStacked ? "flex-col text-center gap-1.5" : "flex-row",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {markComponent}
      <div className="inline-flex items-baseline font-extrabold tracking-tight font-sans">
        <span className={textColor} style={{ fontSize: `${size * 0.85}px` }}>nexty</span>
        <motion.span
          className="text-blue-600 dark:text-blue-500"
          style={{ fontSize: `${size * 0.85}px`, color: "#2563EB" }}
          animate={{ scale: isActive ? [1, 1.4, 1] : 1 }}
          transition={{ duration: 0.6, repeat: Infinity }}
        >
          .
        </motion.span>
      </div>
    </motion.div>
  );
}

/**
 * Loader / Spinner de Marque Produit nexty
 */
export function NextyBrandSpinner({ size = 44, className }: { size?: number; className?: string }) {
  return (
    <div className={cn("inline-flex items-center justify-center p-2", className)}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      >
        <NextyBrandMark size={size} color="gradient" />
      </motion.div>
    </div>
  );
}
